/**
 * Product Options Price Manager
 *
 * Display total = immutable base variant price + sum of registered add-ons.
 * Addon formulas are unchanged; this file only controls when totals are written.
 * Strike/saving elements ([data-compare-cents] / [data-saving-cents]) move with
 * the same addon sum, so compare - total (= base discount) stays constant.
 * Until accessory variants carry their own compare_at_price, the addon part of
 * the strike falls back to current prices (contributes no extra saving).
 *
 * Register plugins with:
 *   PriceManager.register({ id, getAddonCents, containerSelector, triggerSelectors, async })
 */
(function () {
  'use strict';

  var DEBUG = false;
  function log() {
    if (DEBUG) console.log.apply(console, arguments);
  }

  var PriceManager = (function () {
    var _registry = [];
    var _basePrice = 0;
    var _baseCompare = 0;
    var _lastCents = null;
    var _debounce = null;
    var _observers = [];
    var _variantUnsub = null;
    var _started = false;
    var _rebindFrame = 0;

    function register(opts) {
      if (!opts || !opts.id || typeof opts.getAddonCents !== 'function') {
        console.warn('[PriceManager] invalid registration — need id + getAddonCents()');
        return;
      }
      for (var i = 0; i < _registry.length; i++) {
        if (_registry[i].id === opts.id) return;
      }
      _registry.push(opts);
      log('[PriceManager] registered "' + opts.id + '"');
    }

    function start() {
      if (_started) return;
      if (_registry.length === 0) return;

      _readFreshBasePrice();
      _bindGlobalEvents();
      _bindObservers();
      _bindVariantChange();

      _started = true;
      log(
        '[PriceManager] started — base:' +
          _basePrice +
          'c  plugins:' +
          _registry.map(function (r) {
            return r.id;
          }).join(', ')
      );

      // One immediate pass, plus a short follow-up for async option UIs (YMQ).
      _recalculate();
      setTimeout(_recalculate, 50);
    }

    function _readFreshBasePrice() {
      var bp = document.querySelector('product-buy-price');
      if (bp) {
        var raw = parseInt(bp.getAttribute('data-price'), 10);
        if (!isNaN(raw)) {
          _basePrice = raw;
          _readBaseCompare(); // 无参 → 从页面上的 [data-compare-cents] 锚点读基础原价
          return;
        }
      }
      var jsonEl = document.querySelector('variant-selects [data-selected-variant]');
      if (jsonEl) {
        try {
          var v = JSON.parse(jsonEl.textContent);
          if (v && typeof v.price === 'number') {
            _basePrice = v.price;
            _readBaseCompare(v.compare_at_price);
            return;
          }
        } catch (_) {}
      }
      log('[PriceManager] WARN: could not read base price');
    }

    function _readBaseCompare(fromVariant) {
      var cents = parseInt(fromVariant, 10);
      if (isNaN(cents)) {
        // Variant data unavailable: read the Liquid-stamped strike anchor instead.
        var strikeEl = document.querySelector('[data-compare-cents]');
        cents = strikeEl ? parseInt(strikeEl.getAttribute('data-compare-cents'), 10) : NaN;
      }
      _baseCompare = isNaN(cents) ? 0 : Math.max(0, cents);
    }

    function _recalculate() {
      var addon = 0;
      var parts = [];
      var sourceValues = {};

      for (var i = 0; i < _registry.length; i++) {
        try {
          var a = _registry[i].getAddonCents();
          if (typeof a === 'number') {
            addon += a;
            sourceValues[_registry[i].id] = a;
            parts.push(_registry[i].id + ':' + a + 'c');
          }
        } catch (e) {
          parts.push(_registry[i].id + ':ERR');
        }
      }

      var combined = _basePrice + addon;
      if (combined < 0) combined = 0;

      if (combined === _lastCents) return;
      _lastCents = combined;

      log(
        '[PriceManager] base:' +
          _basePrice +
          'c  ' +
          parts.join('  ') +
          '  → ' +
          combined +
          'c  (' +
          (combined / 100).toFixed(2) +
          ' €)'
      );

      var fmt = theme.settings.currencyCodeEnabled
        ? theme.settings.moneyWithCurrencyFormat
        : theme.settings.moneyFormat;
      var html = theme.Currency.formatMoney(combined, fmt);

      _updateDOM(combined, html);
      _updateStrikeAndSaving(_baseCompare + addon, combined);

      document.dispatchEvent(
        new CustomEvent('product-price:updated', {
          detail: {
            baseCents: _basePrice,
            addonCents: addon,
            totalCents: combined,
            sources: sourceValues,
            root: document.querySelector('[data-product-set-root]')
          }
        })
      );
    }

    // Strike total = base variant compare + addon sum (same sum the current price uses,
    // so the two displays never drift apart). Saving = strike - total, i.e. the constant
    // base discount until accessories carry their own compare_at_price.
    function _updateStrikeAndSaving(compareCents, totalCents) {
      if (!(compareCents > 0) || compareCents <= totalCents) return;

      var strikes = document.querySelectorAll('[data-compare-cents]');
      if (strikes.length) {
        var compareText = _fmtEur(compareCents);
        for (var i = 0; i < strikes.length; i++) {
          strikes[i].textContent = compareText;
        }
      }

      var savings = document.querySelectorAll('[data-saving-cents]');
      if (savings.length) {
        var savingText = '-' + _fmtEur(compareCents - totalCents);
        for (var j = 0; j < savings.length; j++) {
          savings[j].textContent = savingText;
        }
      }
    }

    var _eurFmt = null;
    function _fmtEur(cents) {
      if (_eurFmt === null) {
        try {
          _eurFmt = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
        } catch (e) {
          _eurFmt = false;
        }
      }
      if (_eurFmt) return _eurFmt.format((cents || 0) / 100);
      return ((cents || 0) / 100).toFixed(2) + ' €';
    }

    function _updateDOM(cents, html) {
      var pw = document.querySelector('[id^="Price-"]');
      if (pw && !pw.querySelector('price-per-item')) {
        var reg = pw.querySelector('.price__regular');
        if (reg) reg.innerHTML = html;
      }

      var sp = document.querySelector('[id^="StickyPrice-"]');
      if (sp && !sp.querySelector('price-per-item')) {
        var stickyReg = sp.querySelector('.price__regular');
        if (stickyReg) stickyReg.innerHTML = html;
      }

      var allBp = document.querySelectorAll('product-buy-price');
      for (var i = 0; i < allBp.length; i++) {
        if (allBp[i].isConnected) {
          allBp[i].setAttribute('data-price', cents);
          allBp[i].innerHTML = html;
        }
      }
    }

    function _schedule(ms) {
      clearTimeout(_debounce);
      _debounce = setTimeout(_recalculate, ms == null ? 32 : ms);
    }

    function _bindGlobalEvents() {
      var selectors = [];
      for (var i = 0; i < _registry.length; i++) {
        var ts = _registry[i].triggerSelectors;
        if (!Array.isArray(ts)) continue;
        for (var j = 0; j < ts.length; j++) {
          if (selectors.indexOf(ts[j]) === -1) selectors.push(ts[j]);
        }
      }

      document.addEventListener(
        'click',
        function (e) {
          var t = e.target;
          if (!t || !t.closest) return;
          for (var i = 0; i < selectors.length; i++) {
            if (t.closest(selectors[i])) {
              _schedule(32);
              return;
            }
          }
          for (var j = 0; j < _registry.length; j++) {
            var cs = _registry[j].containerSelector;
            if (cs && t.closest(cs)) {
              _schedule(32);
              return;
            }
          }
        },
        true
      );

      document.addEventListener(
        'change',
        function (e) {
          var t = e.target;
          if (!t || !t.closest) return;
          for (var i = 0; i < _registry.length; i++) {
            var cs = _registry[i].containerSelector;
            if (cs && t.closest(cs)) {
              _schedule(0);
              return;
            }
          }
        },
        true
      );

      log('[PriceManager] events bound (' + selectors.length + ' selectors)');
    }

    function _bindObservers() {
      for (var i = 0; i < _registry.length; i++) {
        _attachObserver(_registry[i]);
      }
      log('[PriceManager] ' + _observers.length + ' observer(s) active');
    }

    function _attachObserver(reg) {
      var root = document.querySelector(reg.containerSelector);
      if (root) {
        _startObserving(root, reg.id);
        return;
      }

      // Only async plugins (e.g. YMQ) may appear after first paint.
      if (!reg.async) return;

      var tries = 0;
      var t = setInterval(function () {
        tries++;
        root = document.querySelector(reg.containerSelector);
        if (root) {
          clearInterval(t);
          _startObserving(root, reg.id);
        } else if (tries > 20) {
          clearInterval(t);
        }
      }, 100);
    }

    function _startObserving(root, label) {
      var target = root.shadowRoot || root;
      var obs = new MutationObserver(function () {
        _schedule(32);
      });
      obs.observe(target, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: [
          'checked',
          'selected',
          'ymq-option-price',
          'ymq-option-checked',
          'data-ymq-money',
          'data-price',
          'data-quantity',
          'data-addon-cents',
          'class'
        ]
      });
      _observers.push(obs);
      log('[PriceManager] observer on "' + label + '"');
    }

    function _rebindObserversSoon() {
      if (_rebindFrame) cancelAnimationFrame(_rebindFrame);
      _rebindFrame = requestAnimationFrame(function () {
        _rebindFrame = 0;
        _observers.forEach(function (o) {
          o.disconnect();
        });
        _observers = [];
        for (var i = 0; i < _registry.length; i++) {
          _attachObserver(_registry[i]);
        }
      });
    }

    function _bindVariantChange() {
      _variantUnsub = theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.variantChange,
        function (event) {
          if (!event || !event.data || !event.data.variant) return;

          // Theme has already swapped price DOM to the pure variant price.
          // Recalculate immediately with the trusted event base + current addons.
          _basePrice = event.data.variant.price;
          _readBaseCompare(event.data.variant.compare_at_price);
          _lastCents = null;
          log('[PriceManager] variantChange → base:' + _basePrice + 'c');
          _recalculate();
          _rebindObserversSoon();
        }
      );
    }

    function hasContainer(reg) {
      return !!(reg.containerSelector && document.querySelector(reg.containerSelector));
    }

    return {
      register: register,
      start: start,
      _registry: _registry,
      hasContainer: hasContainer
    };
  })();

  /* ── Plugin: YMQ Product Options ───────────────────────────── */
  if (window.ymq_option) {
    PriceManager.register({
      id: 'ymq',
      async: true,
      getAddonCents: function () {
        var euros = 0;

        var boxes = document.querySelectorAll('.ymq-options-box');
        if (boxes.length === 0) {
          var ymqEl = document.querySelector('ymq-option');
          if (ymqEl && ymqEl.shadowRoot) {
            boxes = ymqEl.shadowRoot.querySelectorAll('.ymq-options-box');
          }
        }

        for (var i = 0; i < boxes.length; i++) {
          var box = boxes[i];

          var inputs = box.querySelectorAll(
            'input[ymq-option-price][ymq-option-checked="true"], ' +
              'input[ymq-option-price]:checked'
          );
          for (var j = 0; j < inputs.length; j++) {
            euros += _p(inputs[j].getAttribute('ymq-option-price'));
          }

          var selects = box.querySelectorAll('select');
          for (var k = 0; k < selects.length; k++) {
            if (selects[k].selectedOptions && selects[k].selectedOptions.length) {
              euros += _p(selects[k].selectedOptions[0].getAttribute('ymq-option-price'));
            }
          }

          var opts = box.querySelectorAll('option[ymq-option-price][ymq-option-checked="true"]');
          for (var m = 0; m < opts.length; m++) {
            var parent = opts[m].parentElement;
            if (
              parent &&
              parent.tagName === 'SELECT' &&
              parent.selectedOptions &&
              parent.selectedOptions.length &&
              parent.selectedOptions[0] === opts[m]
            ) {
              continue;
            }
            euros += _p(opts[m].getAttribute('ymq-option-price'));
          }
        }

        return Math.round(euros * 100);
      },
      containerSelector: 'ymq-option',
      triggerSelectors: ['.ymq-option-value-item', '.ymq-option-label', '.ymq-option-button-content']
    });
  }

  /* ── Plugin: product set configurator ──────────────────────── */
  PriceManager.register({
    id: 'product-set',
    getAddonCents: function () {
      var root = document.querySelector('[data-product-set-root]');
      if (!root) return 0;
      var cents = parseInt(root.getAttribute('data-addon-cents'), 10);
      return isNaN(cents) ? 0 : Math.max(0, cents);
    },
    containerSelector: '[data-product-set-root]',
    triggerSelectors: ['.product-set-dialog__choice', '[data-product-set-confirm]']
  });

  /* ── Plugin: native add-on products ────────────────────────── */
  PriceManager.register({
    id: 'native-addons',
    getAddonCents: function () {
      var total = 0;
      var c = document.querySelector('[data-native-addons]');
      if (!c) return 0;

      var cbs = c.querySelectorAll('[data-native-addon-checkbox]:checked');
      for (var i = 0; i < cbs.length; i++) {
        var cb = cbs[i];
        var price = parseInt(cb.getAttribute('data-price'), 10);
        if (isNaN(price)) continue;

        var qty = parseInt(cb.getAttribute('data-quantity'), 10);
        if (isNaN(qty) || qty < 1) qty = 1;
        total += price * qty;
      }
      return total;
    },
    containerSelector: '[data-native-addons]',
    triggerSelectors: [
      '[data-native-addon-checkbox]',
      '[data-native-addon-minus]',
      '[data-native-addon-plus]'
    ]
  });

  function whenReady(fn) {
    if (typeof theme !== 'undefined' && theme.Currency && theme.pubsub) {
      fn();
      return;
    }
    var tries = 0;
    var t = setInterval(function () {
      tries++;
      if (typeof theme !== 'undefined' && theme.Currency && theme.pubsub) {
        clearInterval(t);
        fn();
      } else if (tries > 100) {
        clearInterval(t);
      }
    }, 50);
  }

  function shouldStart() {
    for (var i = 0; i < PriceManager._registry.length; i++) {
      var reg = PriceManager._registry[i];
      if (PriceManager.hasContainer(reg) || reg.async) return true;
    }
    return false;
  }

  whenReady(function () {
    if (!shouldStart()) return;

    if (
      PriceManager._registry.some(function (reg) {
        return PriceManager.hasContainer(reg);
      })
    ) {
      PriceManager.start();
      return;
    }

    // Async-only (YMQ): short bounded wait, never a long empty poll.
    var tries = 0;
    var t = setInterval(function () {
      tries++;
      if (
        PriceManager._registry.some(function (reg) {
          return PriceManager.hasContainer(reg);
        })
      ) {
        clearInterval(t);
        PriceManager.start();
      } else if (tries > 20) {
        clearInterval(t);
      }
    }, 100);
  });

  function _p(val) {
    if (!val) return 0;
    var n = parseFloat(val);
    return isNaN(n) ? 0 : n;
  }
})();
